import React, { useEffect, useMemo, useState } from "react";
import SubHeader from "components/SubHeader";
import SubHeaderTitle from "components/SubHeader/SubHeaderTitle";
import SubHeaderBackButton from "components/SubHeader/SubHeaderBackButton";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";

import { TWaterSubscriber } from "types/dto";
import { toast } from "react-toastify";
import { TWaterMetersGridRow } from "components/Grid/grids/WaterMetersGrid/types";
import { useParams } from "react-router-dom";
import { SubscriberInfo, TEditFormTypes } from "./components/SubscriberInfo";
import { ApartmentsInfo } from "./components/ApartmentInfo";
import { WaterMeterInfo } from "./components/WaterMeterInfo";
import { omit, pick } from "lodash";

import { TApartmentGridRow } from "components/Grid/grids/ApartmentsGrid/types";
import { ConfirmationModal } from "components/ConfirmationModal";
import { ApartmentsChooserModal } from "components/ApartmentsChooserModal";
import { MetersChooserModal } from "components/MetersChooserModal";
import Tabs from "components/Tabs";
import { tabsData, TabsValue } from "./constants/tabsData";
import DateRangePicker from "components/DateRangePicker";
import { MeterAggregatedDataInRange } from "../../meters/Details/components/MeterAggregatedDataInRange";
import { MeterReadingDataChart } from "../../meters/Details/components/MeterReadingDataChart";
import { MeterStatus } from "../../meters/Details/components/MeterStatus";
import { MeterAlarms } from "../../meters/Details/components/MeterAlarms";
import { MeterReadingData } from "../../meters/Details/components/MeterReadingData";
import CenterContainer from "components/CenterContainer";
import { MeterInfo } from "../../meters/Details/components/MeterInfo";
import { MeterInstallationHistory } from "../../meters/Details/components/MeterInstallationHistory";
import { useURLTools } from "utils/URLtools";
import { TResponseCode } from "utils/netCall/types";

const SubscriberDetailsView = () => {
  const { t } = useTranslation();
  const { subscriberId } = useParams();
  const { queryParams, addUpdateRemoveQueryParams } = useURLTools();

  const [isGettingData, setIsGettingData] = useState<boolean>(false);
  const [initData, setInitData] = useState<TWaterSubscriber>();
  const [selectedTab, setSelectedTab] = useState<TabsValue>(tabsData[0].value);
  const [subscriberInfoSubmitting, setSubscriberInfoSubmitting] =
    useState(false);
  const [isApartmentsModalOpen, setIsApartmentsModalOpen] = useState(false);
  const [selectedApartment, setSelectedApartment] =
    useState<TApartmentGridRow>();
  const [selectedMeter, setSelectedMeter] = useState<TWaterMetersGridRow>();
  const [isMetersModalOpen, setIsMetersModalOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<
    "apartment" | "assignMeter" | "unassignMeter"
  >();
  const [enableEditing, setEnableEditing] = useState(false);
  const [confirmationSubmitting, setConfirmationSubmitting] = useState(false);

  const confirmationModalDesc = useMemo(() => {
    if (confirmationModal === "apartment") {
      return t("confirmChangeApartmentDesc");
    } else if (confirmationModal === "assignMeter") {
      return t("confirmChangeMeterDesc");
    } else if (confirmationModal === "unassignMeter") {
      return t("confirmUnAssignMeterDesc");
    }
  }, [confirmationModal]);

  const onChooseApartment = (value: TApartmentGridRow) => {
    setSelectedApartment(value);
    setConfirmationModal("apartment");
  };
  const onChooseMeter = (value: TWaterMetersGridRow) => {
    setSelectedMeter(value);
    setConfirmationModal("assignMeter");
  };

  const getSubscriberDetails = async () => {
    setIsGettingData(true);
    const res = await netCall<TWaterSubscriber>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterSubscriber/GetWaterSubscriber`,
      body: {
        subscriberId: subscriberId,
        startDT: queryParams?.from,
        endDT: queryParams?.to,
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setInitData(res.results);
    } else {
      toast.error(t("errorInGettingData"));
    }
    setIsGettingData(false);
  };

  const onSaveSubscriberInfo = async (value: TEditFormTypes) => {
    setSubscriberInfoSubmitting(true);
    const res = await netCall<TWaterSubscriber>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterSubscriber/Update`,
      body: {
        ...pick(initData, ["id", "apartmentId"]),
        ...value,
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setSubscriberInfoSubmitting(false);
      toast.success(t("operationSuccessful"));
      await getSubscriberDetails();
      setEnableEditing(false);
    } else {
      setSubscriberInfoSubmitting(false);
      toast.error(t("operationFailed"));
    }
  };
  const submitChangeMeter = async () => {
    setConfirmationSubmitting(true);
    const res = await netCall<any>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterSubscriber/AddWaterMeter2WaterSubscribe`,
      body: {
        waterMeterId: selectedMeter?.id,
        waterSubscriberId: initData?.id,
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setConfirmationSubmitting(false);
      setIsMetersModalOpen(false);
      setConfirmationModal(undefined);
      toast.success(t("operationSuccessful"));
      await getSubscriberDetails();
    } else {
      setConfirmationSubmitting(false);
      toast.error(t("operationFailed"));
    }
  };
  const submitChangeApartment = async () => {
    setConfirmationSubmitting(true);
    const res = await netCall<any>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterSubscriber/Update`,
      body: {
        ...omit(initData, [
          "apartmentId",
          "currentWaterMeter",
          "installationDT",
          "historicalWaterMeters",
        ]),
        apartmentId: selectedApartment?.id,
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setConfirmationSubmitting(false);
      setIsApartmentsModalOpen(false);
      setConfirmationModal(undefined);
      toast.success(t("operationSuccessful"));
      await getSubscriberDetails();
    } else {
      setConfirmationSubmitting(false);
      toast.error(t("operationFailed"));
    }
  };
  const submitUnassignMeter = async () => {
    setConfirmationSubmitting(true);
    const res = await netCall<any>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterSubscriber/RemoveWaterMeterFromWaterSubscribe`,
      body: {
        waterMeterId: initData?.currentWaterMeter?.waterMeterId,
        waterSubscriberId: initData?.id,
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setConfirmationSubmitting(false);
      setConfirmationModal(undefined);
      toast.success(t("operationSuccessful"));
      await getSubscriberDetails();
    } else {
      setConfirmationSubmitting(false);
      toast.error(t("operationFailed"));
    }
  };

  const onSubmitConfirmation = () => {
    if (confirmationModal === "apartment") {
      submitChangeApartment();
    } else if (confirmationModal === "assignMeter") {
      submitChangeMeter();
    } else if (confirmationModal === "unassignMeter") {
      submitUnassignMeter();
    }
  };

  useEffect(() => {
    if (subscriberId && queryParams?.from && queryParams?.to) {
      getSubscriberDetails();
    }
  }, [subscriberId, queryParams]);

  useEffect(() => {
    if (subscriberId && !queryParams?.from && !queryParams?.to) {
      const today = new Date();
      const priorDate = new Date().setDate(today.getDate() - 7);
      addUpdateRemoveQueryParams([
        {
          parameter: "from",
          value: new Date(priorDate).toISOString().split("T")[0],
        },
        { parameter: "to", value: today.toISOString().split("T")[0] },
      ]);
    }
  }, [subscriberId, queryParams]);

  const renderTabContent = () => {
    if (selectedTab === TabsValue.INFO) {
      return (
        <div
          className={
            "flex flex-col grid-cols-3 gap-y-4 p-4 h-full overflow-auto"
          }
        >
          <div className="col-span-3">
            <SubscriberInfo
              data={initData}
              isLoading={isGettingData}
              onSaveClick={onSaveSubscriberInfo}
              isButtonLoading={subscriberInfoSubmitting}
              enableEditing={enableEditing}
              onEditClick={() => setEnableEditing(true)}
            />
          </div>
          <div className="col-span-3">
            <ApartmentsInfo
              apartmentId={initData?.apartmentId}
              onChangeApartmentClick={() => setIsApartmentsModalOpen(true)}
            />
          </div>
          <div className="col-span-3">
            <WaterMeterInfo
              waterMeterId={initData?.currentWaterMeter?.waterMeterId}
              historicalWaterMetersData={initData?.historicalWaterMeters}
              onAssignMeterClick={() => setIsMetersModalOpen(true)}
              onUnAssignMeterClick={() => setConfirmationModal("unassignMeter")}
            />
          </div>
        </div>
      );
    } else if (selectedTab === TabsValue.USAGE_REPORT) {
      return (
        <>
          <div className={"w-full my-4 pr-4"}>
            <DateRangePicker
              initFromDate={queryParams?.from}
              initToDate={queryParams?.to}
              onChange={(value) => {
                if (value[0]) {
                  addUpdateRemoveQueryParams([
                    { parameter: "from", value: value[0]?.split("T")[0] },
                  ]);
                }
                if (value[1]) {
                  addUpdateRemoveQueryParams([
                    { parameter: "to", value: value[1]?.split("T")[0] },
                  ]);
                }
              }}
              disabled={isGettingData}
            />
          </div>
          {initData?.readingData?.readingData &&
          initData?.readingData?.readingData.length > 0 ? (
            <div
              className={
                "grid grid-cols-3 gap-y-4 px-4 pb-4 h-full overflow-auto gap-x-4"
              }
            >
              <div className={"col-span-1 "}>
                <MeterAggregatedDataInRange
                  title={"کل مصرف مشترک در بازه"}
                  data={initData.readingData}
                  isLoading={isGettingData}
                />
              </div>
              <div className={"col-span-2 "}>
                <MeterReadingDataChart
                  title={"مصرف روز به روز مشترک در بازه"}
                  data={initData.readingData}
                  isLoading={isGettingData}
                />
              </div>
              <div className={"col-span-3"}>
                <MeterReadingData
                  data={initData.readingData}
                  isLoading={isGettingData}
                />
              </div>
            </div>
          ) : (
            <CenterContainer>
              <p className={"text-base"}>
                {t("noDataAvailableForSelectedDateRange")}
              </p>
            </CenterContainer>
          )}
        </>
      );
    }
  };
  return (
    <>
      <SubHeader>
        <SubHeaderTitle>
          <SubHeaderBackButton />
          {t("subscriberDetails")}
        </SubHeaderTitle>
      </SubHeader>
      <Tabs
        tabs={tabsData}
        value={selectedTab}
        onChange={setSelectedTab}
        variant={"fullWidth"}
      />
      {renderTabContent()}
      {isApartmentsModalOpen && (
        <ApartmentsChooserModal
          onClose={() => {
            setIsApartmentsModalOpen(false);
          }}
          isOpen={isApartmentsModalOpen}
          onSelectApartment={onChooseApartment}
        />
      )}
      {isMetersModalOpen && (
        <MetersChooserModal
          onClose={() => {
            setIsMetersModalOpen(false);
          }}
          isOpen={isMetersModalOpen}
          onSelectMeter={onChooseMeter}
        />
      )}
      {confirmationModal && (
        <ConfirmationModal
          isOpen={!!confirmationModal}
          onSubmit={onSubmitConfirmation}
          onClose={() => setConfirmationModal(undefined)}
          isLoading={confirmationSubmitting}
          title={t("acceptance")}
          description={confirmationModalDesc}
          type={confirmationModal === "unassignMeter" ? "warning" : "info"}
        />
      )}
    </>
  );
};

export default SubscriberDetailsView;
